// ---------------------------------------------------------------------------
// Object definitions — the content system.
//
// Every game object references an ObjectDef by id. Defs describe visuals and
// physical extents; they are pure data so new content (imported card sets,
// boards, dice) is just "more defs". Built-in content (a standard 52-card
// deck, poker chips, boards) is generated procedurally so the engine needs
// zero binary assets out of the box.
// ---------------------------------------------------------------------------

export type ObjKind = "card" | "stack" | "token" | "board" | "zone" | "die";

/** How a flat face (card front/back, chip top, board surface) is drawn. */
export type FaceSpec =
  | { type: "proc-card"; rank: string; suit: "S" | "H" | "D" | "C" }
  | { type: "proc-back"; style: string }             // patterned card back
  | { type: "proc-chip"; color: string; value: number | string }
  | { type: "proc-mtgmat"; color?: string; v?: number } // MTG playmat w/ slots (v2 = flat vertical-column mat)
  | { type: "proc-tcgmat"; game: "pokemon" | "lorcana" } // TCG playmat w/ slots
  | { type: "proc-board"; style: "felt" | "grid" | "checker" | "trick" | "backgammon"; color?: string }
  /** One die face: d6 gets pips (or `label` text), other dice a numbered polygon. */
  | { type: "proc-die"; sides: number; value: number; color: string; label?: string }
  /** Domino tile face: `a` pips on top, `b` below the divider. */
  | { type: "proc-domino"; a: number; b: number }
  /** Top-down chess piece: colored disc with the piece glyph. */
  | { type: "proc-piece"; glyph: string; color: string; ink: string }
  | { type: "color"; color: string }
  /** radius (units): clip the art's corners (scans bake white corners). */
  | { type: "image"; url: string; radius?: number }; // imported content

export interface ObjectDef {
  id: string;
  kind: ObjKind;
  name: string;
  /** Full extents [width(x), height(y), depth(z)] in table units (~cm). */
  size: [number, number, number];
  front?: FaceSpec;         // face shown when faceUp
  back?: FaceSpec;          // face shown when faceDown (and when redacted)
  /**
   * Corner rounding in table units for flat box-ish objects (cards, decks,
   * boards). 0 / undefined = square corners; higher cuts the model's corners.
   */
  cornerRadius?: number;
  color?: string;           // base/side color
  shape?: "box" | "cylinder";
  /**
   * Stacking group: objects with the same group merge into a container when
   * dropped on each other (cards → deck, chips → chip stack).
   */
  stackGroup?: string;
  /** For stackable defs: the container def created when two merge. */
  stackDef?: string;
  /** For stack defs: which group they accept. */
  acceptsGroup?: string;
  /** Dice only: number of faces. Rolls are server-side (seeded RNG). */
  sides?: number;
  /** Dice only: face labels shown instead of numbers (labels[value-1]). */
  labels?: readonly string[];
  /**
   * Dice only: a "roll" advances to the next face instead of randomizing —
   * for deliberately-turned dice like the backgammon doubling cube.
   */
  cycle?: boolean;
  /**
   * Def to show when this object's identity is redacted in a hand zone
   * (defaults to "card.hidden"). Lets a hidden domino still look like a
   * domino without leaking which one it is.
   */
  hiddenDef?: string;
  /**
   * Boards only: render as a flat printed mat (a rounded-corner decal lying
   * on the felt) instead of a raised slab. Playmats are flat; wooden boards
   * (chess, backgammon) keep their thickness.
   */
  flat?: boolean;
  /** Chess pieces: which silhouette the 3D renderer builds. */
  piece?: ChessPieceKind;
  /** Free-form metadata (e.g. MTG type_line for deck searching). */
  meta?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export const DEFS = new Map<string, ObjectDef>();

export function registerDef(def: ObjectDef): ObjectDef {
  DEFS.set(def.id, def);
  return def;
}

export function getDef(id: string): ObjectDef {
  const d = DEFS.get(id);
  if (d) return d;
  // Unknown defs (e.g. removed imports) degrade gracefully to a grey token.
  return {
    id,
    kind: "token",
    name: "Unknown",
    size: [4, 1, 4],
    color: "#888888",
    shape: "box",
  };
}

// ---------------------------------------------------------------------------
// Built-in content
// ---------------------------------------------------------------------------

export const CARD_SIZE: [number, number, number] = [6.3, 0.06, 8.8];
export const CARD_THICKNESS = CARD_SIZE[1];
/** Default corner rounding for cards (matches the proc face art radius). */
export const CARD_CORNER = 0.55;

export const SUITS = ["S", "H", "D", "C"] as const;
export const RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"] as const;
const SUIT_NAMES: Record<string, string> = { S: "Spades", H: "Hearts", D: "Diamonds", C: "Clubs" };
const RANK_NAMES: Record<string, string> = {
  A: "Ace", J: "Jack", Q: "Queen", K: "King",
};

/** The 52 standard playing cards: def ids like "card.AS", "card.10H". */
for (const suit of SUITS) {
  for (const rank of RANKS) {
    registerDef({
      id: `card.${rank}${suit}`,
      kind: "card",
      name: `${RANK_NAMES[rank] ?? rank} of ${SUIT_NAMES[suit]}`,
      size: CARD_SIZE,
      cornerRadius: CARD_CORNER,
      front: { type: "proc-card", rank, suit },
      back: { type: "proc-back", style: "red" },
      stackGroup: "cards.standard",
      stackDef: "deck.standard",
    });
  }
}

/** Deck container for standard cards. */
registerDef({
  id: "deck.standard",
  kind: "stack",
  name: "Deck",
  size: CARD_SIZE, // height is dynamic: count × card thickness
  cornerRadius: CARD_CORNER,
  back: { type: "proc-back", style: "red" },
  acceptsGroup: "cards.standard",
});

/** Poker chips. */
export const CHIP_COLORS: Array<{ id: string; color: string; value: number }> = [
  { id: "white", color: "#e8e4da", value: 1 },
  { id: "red", color: "#c0392b", value: 5 },
  { id: "blue", color: "#2465b0", value: 10 },
  { id: "green", color: "#1e8449", value: 25 },
  { id: "black", color: "#23252b", value: 100 },
];

for (const c of CHIP_COLORS) {
  registerDef({
    id: `chip.${c.id}`,
    kind: "token",
    name: `${c.value} chip`,
    size: [3.9, 0.34, 3.9],
    shape: "cylinder",
    color: c.color,
    front: { type: "proc-chip", color: c.color, value: c.value },
    back: { type: "proc-chip", color: c.color, value: c.value },
    stackGroup: `chips.${c.id}`,
    stackDef: `chipstack.${c.id}`,
  });
  registerDef({
    id: `chipstack.${c.id}`,
    kind: "stack",
    name: `${c.value} chips`,
    size: [3.9, 0.34, 3.9],
    shape: "cylinder",
    color: c.color,
    front: { type: "proc-chip", color: c.color, value: c.value },
    back: { type: "proc-chip", color: c.color, value: c.value },
    acceptsGroup: `chips.${c.id}`,
  });
}

/** Checkers pieces — stackable so a doubled piece reads as a king. */
export const CHECKER_COLORS: Array<{ id: string; color: string; name: string }> = [
  { id: "red", color: "#b03a2e", name: "Red checker" },
  { id: "black", color: "#26262c", name: "Black checker" },
];
for (const c of CHECKER_COLORS) {
  registerDef({
    id: `checker.${c.id}`,
    kind: "token",
    name: c.name,
    size: [4.6, 0.8, 4.6],
    shape: "cylinder",
    color: c.color,
    front: { type: "color", color: c.color },
    back: { type: "color", color: c.color },
    stackGroup: `checkers.${c.id}`,
    stackDef: `checkerstack.${c.id}`,
  });
  registerDef({
    id: `checkerstack.${c.id}`,
    kind: "stack",
    name: `${c.name} (king)`,
    size: [4.6, 0.8, 4.6],
    shape: "cylinder",
    color: c.color,
    front: { type: "color", color: c.color },
    back: { type: "color", color: c.color },
    acceptsGroup: `checkers.${c.id}`,
  });
}

/** Polyhedral dice. d6 is ivory with pips; the rest follow TTRPG-set colors. */
export const DICE: Array<{ sides: number; color: string; size: number }> = [
  { sides: 4, color: "#7d5bbe", size: 3.6 },
  { sides: 6, color: "#efe9da", size: 3.2 },
  { sides: 8, color: "#1f9e8e", size: 3.6 },
  { sides: 10, color: "#d97b18", size: 3.7 },
  { sides: 12, color: "#bc4038", size: 3.9 },
  { sides: 20, color: "#2465b0", size: 4.2 },
];

for (const die of DICE) {
  registerDef({
    id: `die.d${die.sides}`,
    kind: "die",
    name: `d${die.sides}`,
    size: [die.size, die.size, die.size],
    color: die.color,
    sides: die.sides,
  });
}

/** Backgammon doubling cube: turned (2→4→…→64), never randomly rolled. */
registerDef({
  id: "die.doubling",
  kind: "die",
  name: "Doubling cube",
  size: [4.4, 4.4, 4.4],
  color: "#efe6d4",
  sides: 6,
  labels: ["2", "4", "8", "16", "32", "64"],
  cycle: true,
});

/** Double-six dominoes. Card-kind so hand redaction, deal and draw all work;
 *  card-thin so the shared card/deck pipelines apply unchanged. */
export const DOMINO_SIZE: [number, number, number] = [3.4, CARD_THICKNESS, 6.8];
for (let a = 0; a <= 6; a++) {
  for (let b = a; b <= 6; b++) {
    registerDef({
      id: `domino.${a}${b}`,
      kind: "card",
      name: `${a}–${b} domino`,
      size: DOMINO_SIZE,
      cornerRadius: 0.4,
      color: "#efe8d8",
      front: { type: "proc-domino", a, b },
      back: { type: "proc-back", style: "bone" },
      stackGroup: "cards.domino",
      stackDef: "deck.domino",
      hiddenDef: "domino.hidden",
    });
  }
}
registerDef({
  id: "deck.domino",
  kind: "stack",
  name: "Domino pile",
  size: DOMINO_SIZE,
  cornerRadius: 0.4,
  color: "#efe8d8",
  back: { type: "proc-back", style: "bone" },
  acceptsGroup: "cards.domino",
});
registerDef({
  id: "domino.hidden",
  kind: "card",
  name: "Domino",
  size: DOMINO_SIZE,
  cornerRadius: 0.4,
  color: "#efe8d8",
  back: { type: "proc-back", style: "bone" },
});

/** Flippable coin (Pokémon rides on coin flips). Rolls like a 2-sided die:
 *  value 1 = heads, 2 = tails. */
registerDef({
  id: "die.coin",
  kind: "die",
  name: "Coin",
  size: [4.6, 0.5, 4.6],
  shape: "cylinder",
  color: "#d1a333",
  sides: 2,
});

/** Pokémon damage counters — stackable tokens in the printed denominations. */
export const DMG_COUNTERS: Array<{ value: number; color: string }> = [
  { value: 10, color: "#e8c545" },
  { value: 50, color: "#e07b28" },
  { value: 100, color: "#c0392b" },
];
for (const c of DMG_COUNTERS) {
  registerDef({
    id: `dmg.${c.value}`,
    kind: "token",
    name: `${c.value} damage`,
    size: [2.7, 0.3, 2.7],
    shape: "cylinder",
    color: c.color,
    front: { type: "proc-chip", color: c.color, value: c.value },
    back: { type: "proc-chip", color: c.color, value: c.value },
    stackGroup: `dmg.${c.value}`,
    stackDef: `dmgstack.${c.value}`,
  });
  registerDef({
    id: `dmgstack.${c.value}`,
    kind: "stack",
    name: `${c.value} damage counters`,
    size: [2.7, 0.3, 2.7],
    shape: "cylinder",
    color: c.color,
    front: { type: "proc-chip", color: c.color, value: c.value },
    back: { type: "proc-chip", color: c.color, value: c.value },
    acceptsGroup: `dmg.${c.value}`,
  });
}

/** Chess pieces. */
export type ChessPieceKind = "pawn" | "rook" | "knight" | "bishop" | "queen" | "king";

export const CHESS_PIECES: Array<{ piece: ChessPieceKind; glyph: string; w: number; h: number; name: string }> = [
  { piece: "pawn", glyph: "♟", w: 3.1, h: 4.2, name: "Pawn" },
  { piece: "rook", glyph: "♜", w: 3.5, h: 4.9, name: "Rook" },
  { piece: "knight", glyph: "♞", w: 3.5, h: 5.4, name: "Knight" },
  { piece: "bishop", glyph: "♝", w: 3.5, h: 6.0, name: "Bishop" },
  { piece: "queen", glyph: "♛", w: 3.9, h: 6.8, name: "Queen" },
  { piece: "king", glyph: "♚", w: 3.9, h: 7.4, name: "King" },
];

export const CHESS_COLORS: Array<{ id: string; color: string; ink: string; name: string }> = [
  { id: "white", color: "#e9e2cf", ink: "#463d32", name: "White" },
  { id: "black", color: "#35312e", ink: "#d9d2c3", name: "Black" },
];

for (const c of CHESS_COLORS) {
  for (const p of CHESS_PIECES) {
    registerDef({
      id: `chess.${c.id}.${p.piece}`,
      kind: "token",
      name: `${c.name} ${p.name.toLowerCase()}`,
      size: [p.w, p.h, p.w],
      shape: "cylinder",
      color: c.color,
      piece: p.piece,
      front: { type: "proc-piece", glyph: p.glyph, color: c.color, ink: c.ink },
      back: { type: "proc-piece", glyph: p.glyph, color: c.color, ink: c.ink },
    });
  }
}

/** Generic pawns/markers. */
export const PAWN_COLORS = ["#e74c3c", "#3498db", "#2ecc71", "#f1c40f", "#9b59b6", "#e67e22"];
PAWN_COLORS.forEach((color, i) => {
  registerDef({
    id: `pawn.${i}`,
    kind: "token",
    name: "Pawn",
    size: [2.4, 3.6, 2.4],
    shape: "cylinder",
    color,
  });
});

/** Boards / mats. */
registerDef({
  id: "board.checker",
  kind: "board",
  name: "Checkerboard",
  size: [48, 0.6, 48],
  front: { type: "proc-board", style: "checker" },
  color: "#4a3524",
});
registerDef({
  id: "board.backgammon",
  kind: "board",
  name: "Backgammon board",
  size: [76, 0.6, 60],
  front: { type: "proc-board", style: "backgammon" },
  color: "#5a3d26",
  cornerRadius: 1.2,
});
registerDef({
  id: "board.trickmat",
  kind: "board",
  name: "Trick mat",
  size: [46, 0.06, 46],
  front: { type: "proc-board", style: "trick" },
  color: "#173d2a",
  cornerRadius: 2.4,
  flat: true,
});
registerDef({
  id: "board.grid",
  kind: "board",
  name: "Grid mat",
  size: [60, 0.4, 60],
  front: { type: "proc-board", style: "grid", color: "#2c3e50" },
  color: "#22303e",
});

/** Hand zone marker def (zones are mostly logic; def carries the label). */
registerDef({
  id: "zone.hand",
  kind: "zone",
  name: "Hand",
  size: [42, 0.1, 13],
});

/** Card-sized slot marker on a playmat (library/graveyard/exile/command). */
registerDef({
  id: "zone.slot",
  kind: "zone",
  name: "Slot",
  size: [7.6, 0.1, 10.2],
});

/** Legacy commander playmat (v1 slab) — kept so old saved rooms still render
 *  with the slot layout their zone objects were placed for. */
registerDef({
  id: "board.mtgmat",
  kind: "board",
  name: "Commander playmat",
  size: [64, 0.5, 42],
  front: { type: "proc-mtgmat" },
  color: "#20242c",
  cornerRadius: 1.6,
});

/**
 * MTG playmat v2: a flat printed mat (no slab) with the four zones stacked in
 * a vertical column on the right. New MTG tables build with this one.
 */
registerDef({
  id: "board.mtgmat2",
  kind: "board",
  name: "MTG playmat",
  size: [68, 0.06, 50],
  front: { type: "proc-mtgmat", v: 2 },
  color: "#20242c",
  cornerRadius: 3,
  flat: true,
});

/** Pokémon playmat: active/bench rows plus deck, discard and prize slots. */
registerDef({
  id: "board.pokemat",
  kind: "board",
  name: "Pokémon playmat",
  size: [66, 0.06, 32],
  front: { type: "proc-tcgmat", game: "pokemon" },
  color: "#28303e",
  cornerRadius: 2.4,
  flat: true,
});

/** Lorcana playmat: play area plus deck, discard and inkwell slots. */
registerDef({
  id: "board.lorcanamat",
  kind: "board",
  name: "Lorcana playmat",
  size: [66, 0.06, 32],
  front: { type: "proc-tcgmat", game: "lorcana" },
  color: "#241f3d",
  cornerRadius: 2.4,
  flat: true,
});

/**
 * The def used when an object's identity is redacted (a card in another
 * player's hand). Only the back is ever rendered.
 */
registerDef({
  id: "card.hidden",
  kind: "card",
  name: "Card",
  size: CARD_SIZE,
  cornerRadius: CARD_CORNER,
  back: { type: "proc-back", style: "red" },
});
